
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Wrench } from "lucide-react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useRepairsUnified } from "@/domains/repairs/hooks/useRepairsUnified";
import { useClientData } from '@/domains/clients/hooks/useClientData';
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { MaskedInput } from "@/components/ui/masked-input";
import { useAuth } from "@/context/AuthContext";

type AddRepairFormData = {
  clientName: string;
  phoneNumber: string;
  email: string;
  inventoryItem: string;
  repairType: string;
  description: string;
  estimatedCost: number;
  estimatedDate: Date | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notes: string;
};

type AddRepairDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: AddRepairFormData) => void;
}

const AddRepairDialog: React.FC<AddRepairDialogProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { createRepair, loading: repairsLoading } = useRepairsUnified();
  const { clients = [], isLoading: clientsLoading, error: clientsError } = useClientData();



  const [formData, setFormData] = useState<AddRepairFormData>({
    clientName: '',
    phoneNumber: '',
    email: '',
    inventoryItem: '',
    repairType: '',
    description: '',
    estimatedCost: 0,
    estimatedDate: null,
    priority: 'medium',
    notes: ''
  });

  const handleChange = <K extends keyof AddRepairFormData>(field: K, value: AddRepairFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.clientName || !formData.repairType || !formData.description) {
      toast({
        title: "Ошибка",
        description: "Пожалуйста, заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }

    try {
      // Создаем ремонт через хук
      const repairData = {
        rentalPointId: user?.user_metadata?.rental_point_id || 'default', // Обязательное поле
        clientName: formData.clientName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        itemName: formData.inventoryItem || 'Не указано', // Обязательное поле
        repairType: formData.repairType,
        issueDescription: formData.description, // Обязательное поле
        estimatedCost: formData.estimatedCost,
        estimatedCompletionDate: formData.estimatedDate,
        priority: formData.priority,
        notes: formData.notes,
        status: 'pending' // Начальный статус
      };

      await createRepair(repairData);
      
      // Вызываем onSave если передан (для дополнительной логики)
      if (onSave) {
        onSave(formData);
      }
      
      // Сбрасываем форму
      setFormData({
        clientName: '',
        phoneNumber: '',
        email: '',
        inventoryItem: '',
        repairType: '',
        description: '',
        estimatedCost: 0,
        estimatedDate: null,
        priority: 'medium',
        notes: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating repair:', error);
      // Ошибка уже показана в хуке через toast
    }
  };

  const REPAIR_TYPES = [
    { id: 'cleaning', name: 'Чистка' },
    { id: 'repair', name: 'Ремонт' },
    { id: 'maintenance', name: 'Техническое обслуживание' },
    { id: 'replacement', name: 'Замена деталей' },
    { id: 'calibration', name: 'Калибровка' },
    { id: 'diagnostics', name: 'Диагностика' }
  ];

  const PRIORITIES = [
    { id: 'low', name: 'Низкий' },
    { id: 'medium', name: 'Средний' },
    { id: 'high', name: 'Высокий' },
    { id: 'urgent', name: 'Срочный' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] max-w-[95vw] md:max-w-[66.666vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader className="text-left px-4 py-3 md:px-6 md:py-4">
          <DialogTitle className="flex items-center text-lg md:text-xl font-semibold">
            <Wrench className="mr-3 h-5 w-5 text-blue-600" />
            Добавить заявку на ремонт
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-2 md:px-6 md:py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client Name */}
            <div className="space-y-2">
              <Label htmlFor="clientName">Имя клиента *</Label>
              {clientsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={formData.clientName}
                  onValueChange={(value) => {
                    const selectedClient = clients.find(c => c.id === value);
                    handleChange("clientName", selectedClient?.fullName || '');
                    if (selectedClient) {
                      handleChange("phoneNumber", selectedClient.phone || '');
                      handleChange("email", selectedClient.email || '');
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите клиента" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.fullName || 'Без имени'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Номер телефона</Label>
              <MaskedInput
                id="phoneNumber"
                mask="+9 (999) 999-99-99"
                value={formData.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                placeholder="+7 (999) 123-45-67"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="example@email.com"
              />
            </div>

            {/* Inventory Item */}
            <div className="space-y-2">
              <Label htmlFor="inventoryItem">Предмет ремонта</Label>
              <Input
                id="inventoryItem"
                value={formData.inventoryItem}
                onChange={(e) => handleChange("inventoryItem", e.target.value)}
                placeholder="Название предмета"
              />
            </div>

            {/* Repair Type */}
            <div className="space-y-2">
              <Label htmlFor="repairType">Тип ремонта *</Label>
              <Select
                value={formData.repairType}
                onValueChange={(value) => handleChange("repairType", value)}
              >
                <SelectTrigger id="repairType">
                  <SelectValue placeholder="Выберите тип ремонта" />
                </SelectTrigger>
                <SelectContent>
                  {REPAIR_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Приоритет</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleChange("priority", value as AddRepairFormData['priority'])}
              >
                <SelectTrigger id="priority">
                  <SelectValue placeholder="Выберите приоритет" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((priority) => (
                    <SelectItem key={priority.id} value={priority.id}>
                      {priority.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2 col-span-1 md:col-span-2">
              <Label htmlFor="description">Описание проблемы *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Подробное описание проблемы"
                rows={3}
              />
            </div>

            {/* Estimated Cost */}
            <div className="space-y-2">
              <Label htmlFor="estimatedCost">Ориентировочная стоимость (₽)</Label>
              <Input
                id="estimatedCost"
                type="number"
                min="0"
                step="0.01"
                value={formData.estimatedCost}
                onChange={(e) => handleChange("estimatedCost", parseFloat(e.target.value) || 0)}
              />
            </div>

            {/* Estimated Date */}
            <div className="space-y-2">
              <Label htmlFor="estimatedDate">Ориентировочная дата готовности</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.estimatedDate && "text-muted-foreground"
                    )}
                    id="estimatedDate"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.estimatedDate ? (
                      format(formData.estimatedDate, "PPP", { locale: ru })
                    ) : (
                      <span>Выберите дату</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.estimatedDate || undefined}
                    onSelect={(date) => handleChange("estimatedDate", date)}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2 col-span-1 md:col-span-2">
              <Label htmlFor="notes">Дополнительные заметки</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Дополнительная информация"
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 py-3 md:px-6 md:py-4 flex flex-col-reverse gap-2 md:flex-row md:justify-end md:gap-0">
          <Button variant="outline" onClick={onClose} className="w-full md:w-auto">
            Отмена
          </Button>
          <Button onClick={handleSave} className="w-full md:w-auto">
            Создать заявку
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRepairDialog;
